import React, { useState, useEffect, useRef, useMemo } from 'react';
import useWebSocket from '../../hooks/wsHook';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useAtom, useSetAtom } from 'jotai';
import { useNavigate, useParams } from 'react-router-dom';
import moment, { Moment } from 'moment-timezone';
import { sensorInfoAtom, SensorInfo, ParsedSensorInfo, FridgeTemp, JInfo, Esensor, ExtendedDeviceEUIProps, eSensorChartAtom, EsensorChart } from '../atoms-types/eSensors';
import {
  Card, Typography, CardContent, Box, Skeleton,
  Button, Stack, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import EsensorChartHandler from '../handlers/eSensorGraph-handler';
import { endpointLink, websocketLink } from '../../index';
import HvacDashboard from '../hvac/HvacDashboard';
import EnvironSensorCard from './EnvironSensorCard';
import {
  getHvacStatus,
  HVAC_STATUS_COLORS,
} from '../hvac/hvacStatus';
import {
  isSensorOffline,
  SENSOR_OFFLINE_COLOR,
  SENSOR_ONLINE_COLOR,
} from '../atoms-types/sensorFreshness';

// Compact summary pill for the online/offline counts, styled to match the
// HVAC dashboard's healthy/alarm chips.
const StatusSummaryChip: React.FC<{ label: string; value: string | number; color?: string }> = ({
  label,
  value,
  color,
}) => (
  <Chip
    variant="outlined"
    label={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {color ? <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: color }} /> : null}
        <Typography component="span" variant="body2" sx={{ fontWeight: 700 }}>
          {value}
        </Typography>
        <Typography component="span" variant="body2" color="text.secondary">
          {label}
        </Typography>
      </Box>
    }
    sx={{ height: 36, borderRadius: 1.5, borderColor: 'divider', '& .MuiChip-label': { px: 1.25 } }}
  />
);

const HVAC_SENSOR_COLORS = [
  '#1E88E5',
  '#43A047',
  '#8E24AA',
  '#F4511E',
  '#00897B',
  '#6D4C41',
  '#3949AB',
  '#D81B60',
];

// Human-friendly page titles per sensor group.
const PAGE_TITLES: Record<string, string> = {
  weather: 'Weather',
  water: 'Water',
  soil: 'Soil',
  tank: 'Tank & Kiln',
};

// Skeleton card component for loading state
const SkeletonCard = () => (
  <Card sx={{ height: '100%', width: '100%' }}>
    <CardContent>
      <Skeleton variant="text" height={40} />
      <Skeleton variant="text" height={40} />
      <Skeleton variant="text" height={40} />
      <Box sx={{ mt: 2 }}>
        <Skeleton variant="rectangular" height={3} />
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
          <Skeleton variant="text" width={100} />
          <Skeleton variant="text" width={20} />
          <Skeleton variant="text" width={100} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const EnvironGrid: React.FC = () => {
  const { Group } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [sensorInfo, setSensorInfo] = useAtom(sensorInfoAtom);
  const [selectedSensor, setSelectedSensor] = useState<ExtendedDeviceEUIProps | null>(null);
  const { messages } = useWebSocket(websocketLink, ['sensor']);
  const setEsensorChart = useSetAtom(eSensorChartAtom);
  const [selectedRange, setSelectedRange] = useState<string>("Last 24 hours");
  const [startDateTime, setStartDateTime] = useState<Moment>(moment().subtract(24, 'hour').tz('America/New_York'));
  const [endDateTime, setEndDateTime] = useState<Moment>(moment().tz('America/New_York'));

  // Sensor-edit access is Admin/Super, matching backend role checks.
  const { user } = useAuth0();
  const userRoles: string[] = (user?.['https://odp.golf/roles'] as string[] | undefined) || [];
  const canEditSensors = userRoles.includes('Admin') || userRoles.includes('Super');

  // Track the last processed message index to avoid reprocessing
  const lastProcessedIndexRef = useRef(-1);

  // Clear query cache when navigating away from environment pages
  useEffect(() => {
    return () => {
      queryClient.removeQueries({ queryKey: ['sensorMetadata'] });
      setSensorInfo({});
      setEsensorChart([]);
    };
  }, [queryClient, setSensorInfo, setEsensorChart]);

  // Effect for updating sensor info from websocket.
  // Batch all new messages into a single setSensorInfo call so one WS tick
  // can't storm re-renders (especially after a selectedSensor change used to
  // reset the cursor and replay the whole message history).
  useEffect(() => {
    if (messages.length === 0) return;

    const newMessages = messages.slice(lastProcessedIndexRef.current + 1);
    if (newMessages.length === 0) return;
    lastProcessedIndexRef.current = messages.length - 1;

    const updates = new Map<string, JInfo>();
    newMessages.forEach((messageObj) => {
      const { channel, message } = messageObj;
      try {
        if (channel === 'sensor' && message) {
          const { deviceEUI, ...jInfo } = message as Esensor;
          if (deviceEUI) {
            updates.set(deviceEUI, jInfo as JInfo);
          }
        }
      } catch (error) {
        console.error('Failed to process websocket message:', error);
      }
    });

    if (updates.size === 0) return;

    // A live reading means the device just reported, so bump LastRx to now —
    // this keeps the "last seen"/offline indicator current without a refetch.
    const nowIso = new Date().toISOString();
    setSensorInfo((prev) => {
      let next: typeof prev | null = null;
      updates.forEach((jInfo, deviceEUI) => {
        if (!prev[deviceEUI]) return;
        if (!next) next = { ...prev };
        next[deviceEUI] = prev[deviceEUI].map((sensor) => ({
          ...sensor,
          jInfo,
          LastRx: nowIso,
        }));
      });
      return next ?? prev;
    });
  }, [messages, setSensorInfo]);

  // Re-render periodically so relative "last seen" text and the offline
  // threshold (1h) stay current even when a quiet sensor sends no updates.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNowTick((n) => n + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Only reset the WS cursor when the page group changes — never on card
  // selection. Resetting on selectedSensor caused the next message to replay
  // every buffered WS update and blank the page.
  useEffect(() => {
    lastProcessedIndexRef.current = messages.length - 1;
  }, [Group]);

  // Query for sensor data. Fetch once per group on load (or when the group
  // changes); after that the sensorInfo atom is kept current by live websocket
  // updates, so we never want a background/focus/reconnect refetch rebuilding
  // the whole atom out from under the live state (that read as a "page reload").
  const { data, isLoading } = useQuery({
    queryKey: ['sensorMetadata', Group],
    queryFn: async () => {
      const response = await axios.get<SensorInfo[]>(`${endpointLink}sensor/${Group}`);
      return response.data;
    },
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  // Prefetch data for other environment groups
  useEffect(() => {
    const prefetchGroups = ['weather', 'water', 'soil'].filter(group => group !== Group);
    prefetchGroups.forEach(group => {
      queryClient.prefetchQuery({
        queryKey: ['sensorMetadata', group],
        queryFn: async () => {
          const response = await axios.get<SensorInfo[]>(`${endpointLink}sensor/${group}`);
          return response.data;
        }
      });
    });
  }, [Group, queryClient]);

  // Set a default selected sensor when data first loads (or Group changes).
  // Do not overwrite an existing in-page selection — that was fighting live updates.
  useEffect(() => {
    if (!data || data.length === 0) return;

    setSelectedSensor((prev) => {
      if (prev?.deviceEUI && data.some((s) => s.deviceEUI === prev.deviceEUI)) {
        return prev;
      }

      const pick = Group === 'hvac'
        ? [...data].sort((a, b) => {
            const rank = { alarm: 0, warning: 1, offline: 2, normal: 3 } as const;
            const statusOf = (s: SensorInfo) => {
              try {
                const info = typeof s.jInfo === 'string' ? JSON.parse(s.jInfo) : s.jInfo;
                return getHvacStatus(
                  typeof info?.TempF === 'number' ? info.TempF : null,
                  s.LastRx,
                  { warningF: s.warningF, alarmF: s.alarmF },
                );
              } catch {
                return 'offline' as const;
              }
            };
            return rank[statusOf(a)] - rank[statusOf(b)];
          })[0]
        : data[0];

      const displays = Array.isArray(pick.Display) ? pick.Display : [];
      if (displays.length === 0) return prev;

      let tempF: number | undefined;
      try {
        const info = typeof pick.jInfo === 'string' ? JSON.parse(pick.jInfo) : pick.jInfo;
        if (typeof info?.TempF === 'number') tempF = info.TempF;
      } catch { /* ignore */ }

      return {
        deviceEUI: pick.deviceEUI,
        Display: displays,
        subClass: pick.Type === "Weather" ? "temp" : null,
        sensorColor: getCardLineColor(
          pick.deviceEUI,
          displays[0],
          pick.Type,
          tempF,
          pick.warningF,
          pick.alarmF,
        ),
      };
    });
  }, [data, Group]);

  const updateSensorInfo = (newSensorInfo: { [deviceEUI: string]: SensorInfo[] }) => { 
    const updatedSensorInfo: { [deviceEUI: string]: ParsedSensorInfo[] } = {};
    
    Object.entries(newSensorInfo).forEach(([deviceEUI, sensors]) => {
      updatedSensorInfo[deviceEUI] = sensors.map(sensor => {
        try {
          const parsedSensor = {
            ...sensor,
            Class: typeof sensor.Class === 'string' ? JSON.parse(sensor.Class) : sensor.Class,
            Display: typeof sensor.Display === 'string' ? JSON.parse(sensor.Display) : sensor.Display,
            jInfo: typeof sensor.jInfo === 'string' ? JSON.parse(sensor.jInfo) : sensor.jInfo
          };
          return parsedSensor as ParsedSensorInfo;
        } catch (error) {
          console.error(`Error parsing sensor data for deviceEUI ${deviceEUI}:`, error);
          // Return a default JInfo object based on the sensor type
          const defaultSensor = {
            ...sensor,
            Class: typeof sensor.Class === 'string' ? JSON.parse(sensor.Class) : sensor.Class,
            Display: typeof sensor.Display === 'string' ? JSON.parse(sensor.Display) : sensor.Display,
            jInfo: {} as JInfo // Default empty JInfo object
          };
          return defaultSensor as ParsedSensorInfo;
        }
      });
    });
    
    console.log("Final updatedSensorInfo:", updatedSensorInfo);
    setSensorInfo(updatedSensorInfo);

    // Seed selection only when nothing valid is selected yet.
    setSelectedSensor((prev) => {
      if (prev?.deviceEUI && updatedSensorInfo[prev.deviceEUI]?.length) {
        return prev;
      }

      const allSensors = Object.values(updatedSensorInfo)
        .map((sensors) => sensors?.[0])
        .filter((s): s is ParsedSensorInfo => Boolean(s));
      const pick = Group === 'hvac'
        ? [...allSensors].sort((a, b) => {
            const rank = { alarm: 0, warning: 1, offline: 2, normal: 3 } as const;
            const statusOf = (s: ParsedSensorInfo) =>
              getHvacStatus(
                typeof (s.jInfo as FridgeTemp | undefined)?.TempF === 'number'
                  ? (s.jInfo as FridgeTemp).TempF
                  : null,
                s.LastRx,
                { warningF: s.warningF, alarmF: s.alarmF },
              );
            return rank[statusOf(a)] - rank[statusOf(b)];
          })[0]
        : allSensors[0];

      if (!pick) return prev;
      const displays = Array.isArray(pick.Display) ? pick.Display : [];
      if (displays.length === 0) return prev;

      const tempF = (pick.jInfo as FridgeTemp | undefined)?.TempF;
      return {
        deviceEUI: pick.deviceEUI,
        Display: displays,
        subClass: pick.Type === "Weather" ? "temp" : null,
        sensorColor: getCardLineColor(
          pick.deviceEUI,
          displays[0],
          pick.Type,
          typeof tempF === 'number' ? tempF : undefined,
          pick.warningF,
          pick.alarmF,
        ),
      };
    });
  }

  // Rebuild the sensorInfo atom from the current group's query data whenever the
  // group changes. This must live in an effect (not inside queryFn): with
  // staleTime: Infinity, revisiting an already-fetched group is a cache hit, so
  // queryFn never re-runs — previously that left the shared atom (and thus the
  // page) showing whichever group was fetched last (e.g. HVAC showing tank/kiln
  // sensors after visiting Tank & Kiln). Live websocket updates still mutate the
  // atom in place afterward.
  useEffect(() => {
    if (!data) return;
    const grouped: { [deviceEUI: string]: SensorInfo[] } = {};
    data.forEach((sensor) => {
      if (!grouped[sensor.deviceEUI]) grouped[sensor.deviceEUI] = [];
      grouped[sensor.deviceEUI].push(sensor);
    });
    updateSensorInfo(grouped);
    // Only re-run when the fetched data (i.e. the group) changes; updateSensorInfo
    // is a stable in-component helper.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Type mapping kept only for chart color selection helpers below.
  // One entry per rendered card, in display order. Weather sensors expand into
  // two cards (temp/humidity and wind/pressure); every other sensor is a single
  // card. This preserves the existing per-card groupings while letting us lay
  // the cards out in a plain CSS grid.
  const cardDescriptors = useMemo(() => {
    const items: { key: string; deviceEUI: string; cardType: 'temp' | 'wind' | null }[] = [];
    Object.entries(sensorInfo).forEach(([deviceEUI, sensors]) => {
      sensors.forEach((sensor) => {
        if (sensor.Type === 'Weather') {
          items.push({ key: `${deviceEUI}-temp`, deviceEUI, cardType: 'temp' });
          items.push({ key: `${deviceEUI}-wind`, deviceEUI, cardType: 'wind' });
        } else {
          items.push({ key: deviceEUI, deviceEUI, cardType: null });
        }
      });
    });
    return items;
  }, [sensorInfo]);

  // Get the color based on the display type and sensor type
  const getColor = (displayType: string, sensorType?: string) => {
    // First check sensor type for specific sensors
    if (sensorType === "SoilMt") {
      return '#795548'; // Medium brown color for soil sensors
    }
    if (sensorType === "Kiln") {
      return 'error.main'; // Error color for kiln sensors
    }
    if (sensorType === "Tank") {
      return '#2196f3'; // Blue color for tank sensors
    }

    // Then check display type for other sensors
    switch (displayType) {
      case 'TempC':
      case 'TempF':
        return 'success.main';
      case 'Humidity':
        return 'warning.main';
      case 'WindAvg':
      case 'WindMax':
      case 'WindDir':
      case 'BaroPress':
        return '#2196f3'; // Blue color for wind-related displays
      case 'Height':
        return '#9c27b0'; // Purple color for river height
      case 'Inches':
        return 'error.main';
      default:
        return 'primary.main';
    }
  };

  const getHvacColorByDevice = (deviceEUI: string) => {
    const hash = deviceEUI.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return HVAC_SENSOR_COLORS[hash % HVAC_SENSOR_COLORS.length];
  };

  const getCardLineColor = (
    deviceEUI: string,
    displayType: string,
    sensorType?: string,
    tempF?: number,
    warningF?: number | null,
    alarmF?: number | null,
  ) => {
    if (sensorType === 'FridgeTemp') {
      if (typeof tempF === 'number') {
        return HVAC_STATUS_COLORS[getHvacStatus(tempF, undefined, { warningF, alarmF })];
      }
      return getHvacColorByDevice(deviceEUI);
    }
    return getColor(displayType, sensorType);
  };

  const isHvacPage = Group === 'hvac';
  const pageTitle = PAGE_TITLES[Group ?? ''] ?? (Group ? `${Group[0].toUpperCase()}${Group.slice(1)}` : 'Sensors');

  const selectHvacSensor = (sensor: ParsedSensorInfo) => {
    const displays = Array.isArray(sensor.Display) ? sensor.Display : [];
    if (displays.length === 0) return;
    const tempF = (sensor.jInfo as FridgeTemp | undefined)?.TempF;
    setSelectedSensor({
      deviceEUI: sensor.deviceEUI,
      Display: displays,
      subClass: null,
      sensorColor: getCardLineColor(
        sensor.deviceEUI,
        displays[0],
        sensor.Type,
        tempF,
        sensor.warningF,
        sensor.alarmF,
      ),
    });
  };

  // Build the list of sensors currently rendered on the page. One entry per
  // deviceEUI, taking the first sensor record (which is what the cards use).
  const sensorsOnPage = useMemo(() => {
    return Object.entries(sensorInfo)
      .map(([deviceEUI, sensors]) => sensors?.[0])
      .filter((s): s is ParsedSensorInfo => Boolean(s));
  }, [sensorInfo]);

  // Online/offline rollup for the summary chips. Recomputed on nowTick so a
  // quiet sensor flips to "offline" once it crosses the 1h threshold.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const statusSummary = useMemo(() => {
    const total = sensorsOnPage.length;
    let offline = 0;
    sensorsOnPage.forEach((s) => {
      if (isSensorOffline(s.LastRx)) offline += 1;
    });
    const online = total - offline;
    return {
      total,
      online,
      offline,
      onlinePct: total ? Math.round((online / total) * 100) : 0,
    };
  }, [sensorsOnPage, nowTick]);

  const openSensorsEditor = () => {
    const group = (Group || 'all').trim() || 'all';
    navigate(`/sensors/edit?group=${encodeURIComponent(group)}`);
  };

  // Render a single sensor card. `cardType` selects the Weather sub-card
  // (temp/humidity vs wind/pressure); pass null for every other sensor type.
  // Used both in the card grid and (for the current selection) in the primary
  // viewport beside the graph.
  const renderSensorCard = (
    deviceEUI: string,
    cardType: 'temp' | 'wind' | null,
    opts?: { selected?: boolean },
  ) => {
    const sensors = sensorInfo[deviceEUI];
    if (!sensors || sensors.length === 0) return null;
    const sensor = sensors[0];
    if (!sensor.jInfo) return null;

    const allDisplays = Array.isArray(sensor.Display) ? sensor.Display : [];
    const cardDisplays = cardType === 'temp'
      ? allDisplays.filter((d) => ['TempF', 'Humidity'].includes(d))
      : cardType === 'wind'
        ? allDisplays.filter((d) => ['WindAvg', 'WindMax', 'WindDir', 'BaroPress'].includes(d))
        : allDisplays;

    if (sensor.Type !== 'FridgeTemp' && cardDisplays.length === 0) return null;

    const displaysForCard = cardDisplays.length > 0 ? cardDisplays : allDisplays;
    const lineColorSource = displaysForCard[0] ?? allDisplays[0];
    const lineColor = lineColorSource
      ? getCardLineColor(
          deviceEUI,
          lineColorSource,
          sensor.Type,
          undefined,
          sensor.warningF,
          sensor.alarmF,
        )
      : 'primary.main';

    const handleSelect = () => {
      if (allDisplays.length === 0) return;
      setSelectedSensor({
        deviceEUI,
        Display: allDisplays,
        subClass: cardType,
        sensorColor: lineColor,
      });
    };

    return (
      <EnvironSensorCard
        sensor={sensor}
        cardType={cardType}
        cardDisplays={displaysForCard}
        selected={opts?.selected}
        startDateTime={startDateTime}
        endDateTime={endDateTime}
        onSelect={handleSelect}
      />
    );
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        height: '100vh',
        width: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          background: '#f1f1f1',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          background: '#888',
          borderRadius: '4px',
          '&:hover': {
            background: '#555',
          },
        },
      }}
    >
      {isHvacPage ? (
        <HvacDashboard
          sensors={sensorsOnPage}
          isLoading={isLoading}
          selectedSensor={selectedSensor}
          onSelectSensor={selectHvacSensor}
          startDateTime={startDateTime}
          endDateTime={endDateTime}
          setStartDateTime={setStartDateTime}
          setEndDateTime={setEndDateTime}
          selectedRange={selectedRange}
          setSelectedRange={setSelectedRange}
          canEditSensors={canEditSensors}
          onEditSensors={openSensorsEditor}
        />
      ) : (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: { xs: 1.5, md: 2 },
          pb: 4,
          minHeight: '100%',
          width: '100%',
        }}
      >
        {/* Header: title + time range + edit locations */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {pageTitle}
          </Typography>
          {canEditSensors ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<EditIcon />}
              onClick={openSensorsEditor}
              disabled={sensorsOnPage.length === 0}
            >
              Edit Sensors
            </Button>
          ) : null}
        </Box>

        {/* Online/offline summary */}
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          {isLoading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rounded" width={110} height={36} />
            ))
          ) : (
            <>
              <StatusSummaryChip label="Sensors" value={statusSummary.total} />
              <StatusSummaryChip label="Online" value={statusSummary.online} color={SENSOR_ONLINE_COLOR} />
              <StatusSummaryChip label="Offline" value={statusSummary.offline} color={SENSOR_OFFLINE_COLOR} />
              <StatusSummaryChip label="Online" value={`${statusSummary.onlinePct}%`} />
            </>
          )}
        </Stack>

        {/* Primary viewport: selected card on the side, graph on top/right */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '300px 1fr', lg: '340px 1fr' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{ minHeight: { xs: 'auto', md: 420 } }}>
            {isLoading ? (
              <SkeletonCard />
            ) : selectedSensor?.deviceEUI ? (
              renderSensorCard(selectedSensor.deviceEUI, selectedSensor.subClass ?? null, { selected: true })
            ) : (
              <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography color="text.secondary">Select a sensor</Typography>
              </Card>
            )}
          </Box>
          <Box sx={{ minHeight: { xs: 360, md: 420 }, '& .MuiCard-root': { height: '100%' } }}>
            <EsensorChartHandler
              deviceEUI={selectedSensor?.deviceEUI || ''}
              Display={selectedSensor?.Display || []}
              subClass={selectedSensor?.subClass || null}
              sensorColor={selectedSensor?.sensorColor}
              sensorInfo={selectedSensor?.deviceEUI ? sensorInfo[selectedSensor.deviceEUI]?.[0] : undefined}
              startDateTime={startDateTime}
              endDateTime={endDateTime}
              setStartDateTime={setStartDateTime}
              setEndDateTime={setEndDateTime}
              selectedRange={selectedRange}
              setSelectedRange={setSelectedRange}
              compact
              chartTitle="Sensor History"
            />
          </Box>
        </Box>

        {/* Card grid — preserves the per-sensor card groupings */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Sensors
          </Typography>
          {isLoading ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1.5 }}>
              {Array.from({ length: 6 }).map((_, i) => (
                <SkeletonCard key={i} />
              ))}
            </Box>
          ) : cardDescriptors.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>
              No sensors on this page.
            </Typography>
          ) : (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
                gap: 1.5,
                alignItems: 'stretch',
              }}
            >
              {cardDescriptors.map((desc) => (
                <Box key={desc.key}>
                  {renderSensorCard(desc.deviceEUI, desc.cardType, {
                    selected:
                      selectedSensor?.deviceEUI === desc.deviceEUI &&
                      (selectedSensor?.subClass ?? null) === desc.cardType,
                  })}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </Box>
      )}

    </Box>
  );
};

export default EnvironGrid;